"use client"

import { useState, useEffect, useRef, useCallback } from "react"
import { createPortal } from "react-dom"
import {
  BellSimple, X, Trash, ArrowClockwise,
  CheckCircle, Info, Warning,
} from "phosphor-react"
import { motion, AnimatePresence } from "framer-motion"
import notificationsService from "../../services/notificationsService"
import { useCurrentUser } from "../../utils/context/UserProvider"
import toast from "react-hot-toast"

const POLL_INTERVAL = 30_000

// ─── helpers ──────────────────────────────────────────────────────────────────

const formatDate = (date) => {
  const d = new Date(date)
  const day = String(d.getDate()).padStart(2, "0")
  const month = d.toLocaleString("en-US", { month: "long" })
  const year = d.getFullYear()
  const hours = String(d.getHours()).padStart(2, "0")
  const minutes = String(d.getMinutes()).padStart(2, "0")
  const seconds = String(d.getSeconds()).padStart(2, "0")
  return `${day} ${month} ${year}, ${hours}:${minutes}:${seconds}`
}

const mergeNotifications = (incoming, prev) => {
  const localReadIds = new Set(prev.filter((n) => n.read).map((n) => n._id))
  return incoming.map((n) => (localReadIds.has(n._id) ? { ...n, read: true } : n))
}

// ─── Icon / colour derived from title ─────────────────────────────────────────

function getNotifAccent(title = "", systemNotification = "") {
  const t = `${title} ${systemNotification}`.toLowerCase()
  if (t.includes("success") || t.includes("created") || t.includes("reinstated") || t.includes("approved"))
    return { color: "emerald", Icon: CheckCircle }
  if (t.includes("rejected") || t.includes("banned") || t.includes("failed"))
    return { color: "red", Icon: Warning }
  if (t.includes("suspended") || t.includes("pending") || t.includes("request"))
    return { color: "yellow", Icon: Info }
  return { color: "blue", Icon: Info }
}

const accentClasses = {
  emerald: {
    iconBg: "bg-emerald-50  dark:bg-emerald-900/20",
    iconFg: "text-emerald-500 dark:text-emerald-400",
    dot: "bg-emerald-400",
    unread: "bg-yellow-50/70 dark:bg-yellow-900/10 hover:bg-yellow-50 dark:hover:bg-yellow-900/20",
  },
  red: {
    iconBg: "bg-red-50    dark:bg-red-900/20",
    iconFg: "text-red-500   dark:text-red-400",
    dot: "bg-yellow-400",
    unread: "bg-yellow-50/70 dark:bg-yellow-900/10 hover:bg-yellow-50 dark:hover:bg-yellow-900/20",
  },
  yellow: {
    iconBg: "bg-yellow-50 dark:bg-yellow-900/20",
    iconFg: "text-yellow-500 dark:text-yellow-400",
    dot: "bg-yellow-400",
    unread: "bg-yellow-50/70 dark:bg-yellow-900/10 hover:bg-yellow-50 dark:hover:bg-yellow-900/20",
  },
  blue: {
    iconBg: "bg-sky-50    dark:bg-sky-900/20",
    iconFg: "text-sky-500   dark:text-sky-400",
    dot: "bg-yellow-400",
    unread: "bg-yellow-50/70 dark:bg-yellow-900/10 hover:bg-yellow-50 dark:hover:bg-yellow-900/20",
  },
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function NotificationDropdown() {
  const [isOpen, setIsOpen] = useState(false)
  const [notifications, setNotifications] = useState([])
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState(new Set())

  const wrapperRef = useRef(null)
  const buttonRef = useRef(null)
  const dropdownRef = useRef(null)
  const pollRef = useRef(null)
  const [anchor, setAnchor] = useState({ top: 0, right: 0 })
  const { currentUser } = useCurrentUser()

  const unreadCount = notifications.filter((n) => !n.read).length

  // ── fetch ─────────────────────────────────────────────────────────────────
  const fetchNotifications = useCallback(async (silent = false) => {
    if (!silent) setLoading(true)
    try {
      const res = await notificationsService.getMy()
      if (res.data?.statusCode === 200) {
        const incoming = res.data.data?.notifications ?? res.data.data ?? []
        setNotifications((prev) => mergeNotifications(incoming, prev))
      }
    } catch (err) {
      console.error("Failed to fetch notifications:", err)
      if (!silent) toast.error("Failed to load notifications")
    } finally {
      if (!silent) setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!currentUser?._id) return
    fetchNotifications()
    pollRef.current = setInterval(() => fetchNotifications(true), POLL_INTERVAL)
    return () => clearInterval(pollRef.current)
  }, [currentUser, fetchNotifications])

  useEffect(() => {
    if (isOpen) fetchNotifications(true)
  }, [isOpen, fetchNotifications])

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") setIsOpen(false) }
    if (isOpen) document.addEventListener("keydown", handler)
    return () => document.removeEventListener("keydown", handler)
  }, [isOpen])

  // Close on outside click (account for portal: dropdown is NOT a DOM child of wrapper)
  useEffect(() => {
    const handler = (e) => {
      const inWrapper = wrapperRef.current?.contains(e.target)
      const inDropdown = dropdownRef.current?.contains(e.target)
      if (!inWrapper && !inDropdown) setIsOpen(false)
    }
    if (isOpen) document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [isOpen])

  // Position the portaled dropdown relative to the bell button
  useEffect(() => {
    if (!isOpen) return
    const update = () => {
      const rect = buttonRef.current?.getBoundingClientRect()
      if (!rect) return
      setAnchor({
        top: rect.bottom + 8,
        right: Math.max(8, window.innerWidth - rect.right),
      })
    }
    update()
    window.addEventListener("resize", update)
    window.addEventListener("scroll", update, true)
    return () => {
      window.removeEventListener("resize", update)
      window.removeEventListener("scroll", update, true)
    }
  }, [isOpen])

  // ── actions ───────────────────────────────────────────────────────────────
  const withBusy = (id, fn) => async () => {
    if (busy.has(id)) return
    setBusy((s) => new Set(s).add(id))
    try { await fn() } finally {
      setBusy((s) => { const n = new Set(s); n.delete(id); return n })
    }
  }

  const markAsRead = (id) => withBusy(id, async () => {
    await notificationsService.markRead(id)
    setNotifications((prev) => prev.map((n) => n._id === id ? { ...n, read: true } : n))
  })()

  const markAllAsRead = async () => {
    if (unreadCount === 0) return
    try {
      await notificationsService.markAllRead()
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })))
    } catch {
      toast.error("Failed to mark all as read")
    }
  }

  const deleteOne = (id) => withBusy(id, async () => {
    await notificationsService.delete(id)
    setNotifications((prev) => prev.filter((n) => n._id !== id))
  })()

  const deleteAll = async () => {
    try {
      await notificationsService.deleteAll()
      setNotifications([])
    } catch {
      toast.error("Failed to clear notifications")
    }
  }

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <div ref={wrapperRef} className="relative">
      {/* Bell button */}
      <motion.button
        ref={buttonRef}
        onClick={() => setIsOpen((o) => !o)}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.93 }}
        className="relative p-2 rounded-full text-neutral-600 dark:text-neutral-300
                   hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
      >
        <BellSimple size={18} weight={isOpen ? "fill" : "regular"} />

        <AnimatePresence>
          {unreadCount > 0 && (
            <motion.span
              key="badge"
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }}
              transition={{ type: "spring", stiffness: 600, damping: 35 }}
              className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1
                         flex items-center justify-center rounded-full
                         bg-yellow-400 text-[9px] font-bold text-neutral-900 leading-none"
            >
              {unreadCount > 99 ? "99+" : unreadCount}
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>

      {/* Dropdown — portaled to <body> so it escapes the navbar's stacking context */}
      {typeof document !== "undefined" && createPortal(
        <AnimatePresence>
          {isOpen && (
            <motion.div
              key="dropdown"
              ref={dropdownRef}
              initial={{ opacity: 0, scale: 0.95, y: -8 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -8 }}
              transition={{ duration: 0.15, ease: "easeOut" }}
              style={{ transformOrigin: "top right", top: anchor.top, right: anchor.right }}
              className="fixed z-[2147483000] flex flex-col
                         w-[380px] max-h-[520px]
                         bg-white dark:bg-neutral-950
                         border border-neutral-200 dark:border-neutral-800
                         rounded-2xl shadow-xl overflow-hidden"
            >
            {/* Header */}
            <div className="flex-shrink-0 flex items-center justify-between px-4 py-3
                            border-b border-neutral-100 dark:border-neutral-800">
              <div className="flex items-center gap-2">
                <BellSimple size={15} weight="regular"
                  className="text-neutral-500 dark:text-neutral-400" />
                <span className="text-sm font-[500] text-neutral-900 dark:text-white">
                  Notifications
                </span>
                {unreadCount > 0 && (
                  <span className="px-1.5 py-0.5 rounded-md
                                   bg-yellow-100 dark:bg-yellow-900/30
                                   text-yellow-700 dark:text-yellow-400
                                   text-[10px] font-bold">
                    {unreadCount} new
                  </span>
                )}
              </div>

              <div className="flex items-center gap-1">
                <button
                  title="Refresh"
                  onClick={() => fetchNotifications()}
                  className="p-1.5 rounded-lg text-neutral-400 hover:text-blue-500
                             hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                >
                  <ArrowClockwise size={13} weight="bold" />
                </button>

                {unreadCount > 0 && (
                  <button
                    onClick={markAllAsRead}
                    className="text-[11px] px-2 py-1 rounded-lg font-medium
                               text-neutral-500 dark:text-neutral-400
                               hover:bg-neutral-100 dark:hover:bg-neutral-800
                               transition-colors"
                  >
                    Mark all read
                  </button>
                )}

                {notifications.length > 0 && (
                  <button
                    title="Clear all"
                    onClick={deleteAll}
                    className="p-1.5 rounded-lg text-neutral-400 hover:text-red-500
                               hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                  >
                    <Trash size={13} weight="bold" />
                  </button>
                )}

                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1.5 rounded-lg text-neutral-400
                             hover:text-neutral-700 dark:hover:text-neutral-200
                             hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
                >
                  <X size={13} weight="bold" />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="divide-y divide-neutral-100 dark:divide-neutral-800/60">
                  {Array.from({ length: 5 }).map((_, i) => <NotifSkeleton key={i} />)}
                </div>

              ) : notifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-14 gap-3">
                  <div className="w-11 h-11 rounded-2xl bg-neutral-100 dark:bg-neutral-800
                                  flex items-center justify-center">
                    <BellSimple size={20} className="text-neutral-400 dark:text-neutral-500" />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-medium text-neutral-600 dark:text-neutral-300">
                      All caught up
                    </p>
                    <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-0.5">
                      No new notifications
                    </p>
                  </div>
                </div>

              ) : (
                <ul className="divide-y divide-neutral-100 dark:divide-neutral-800/60">
                  <AnimatePresence initial={false}>
                    {notifications.map((notif) => (
                      <NotifRow
                        key={notif._id}
                        notif={notif}
                        isBusy={busy.has(notif._id)}
                        onRead={() => !notif.read && markAsRead(notif._id)}
                        onDelete={() => deleteOne(notif._id)}
                      />
                    ))}
                  </AnimatePresence>
                </ul>
              )}
            </div>

            {/* Footer */}
            {!loading && notifications.length > 0 && (
              <div className="flex-shrink-0 px-4 py-2.5
                              border-t border-neutral-100 dark:border-neutral-800">
                <p className="text-[11px] text-neutral-400 dark:text-neutral-500">
                  {notifications.length} notification{notifications.length !== 1 ? "s" : ""}
                  {unreadCount > 0 && ` · ${unreadCount} unread`}
                </p>
              </div>
            )}
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  )
}

// ─── Notification row ─────────────────────────────────────────────────────────

function NotifRow({ notif, isBusy, onRead, onDelete }) {
  const { color, Icon } = getNotifAccent(notif.title, notif.systemNotification)
  const cls = accentClasses[color]

  return (
    <motion.li
      layout
      initial={{ opacity: 0, x: 10 }}
      animate={{ opacity: isBusy ? 0.45 : 1, x: 0 }}
      exit={{ opacity: 0, height: 0, overflow: "hidden" }}
      transition={{ duration: 0.15 }}
      onClick={onRead}
      className={`group relative flex items-start gap-4 px-4 py-3 cursor-pointer select-none
                  transition-colors list-none
                  ${isBusy ? "pointer-events-none" : ""}
                  ${!notif.read
          ? cls.unread
          : "hover:bg-neutral-50 dark:hover:bg-neutral-800/40"
        }`}
    >
      {/* Icon bubble */}
       {!notif.read && (
        <div className={`flex-shrink-0 w-1.5 h-1.5 rounded-full mt-1.5 ${cls.dot}`} />
      )}

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className={`text-[13.5px] text-start leading-snug
                       ${!notif.read
            ? "font-[500] text-neutral-900 dark:text-white"
            : "font-normal text-neutral-600 dark:text-neutral-300"}`}>
          {notif.systemNotification}
        </p>
        <p className="mt-0.5 text-[11px] text-start text-neutral-400 dark:text-neutral-500">
          {formatDate(notif.createdAt)}
        </p>
      </div>

      {/* Unread dot */}
    

      {/* Delete on hover */}
      <button
        onClick={(e) => { e.stopPropagation(); onDelete() }}
        className="absolute right-3 top-1/2 -translate-y-1/2
                   opacity-0 group-hover:opacity-100
                   p-1 rounded-lg text-neutral-400 hover:text-red-500
                   hover:bg-red-50 dark:hover:bg-red-900/20
                   transition-all duration-150"
      >
        <X size={11} weight="bold" />
      </button>
    </motion.li>
  )
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function NotifSkeleton() {
  return (
    <div className="flex items-start gap-3 px-4 py-3 animate-pulse">
      <div className="w-8 h-8 rounded-xl bg-neutral-100 dark:bg-neutral-800 flex-shrink-0" />
      <div className="flex-1 space-y-1.5 pt-0.5">
        <div className="h-3 bg-neutral-100 dark:bg-neutral-800 rounded-md w-3/4" />
        <div className="h-2.5 bg-neutral-100 dark:bg-neutral-800 rounded-md w-2/5" />
      </div>
    </div>
  )
}
